import axios from "axios";
import { STATIC_PROJECTS, STATIC_GALLERY, getStaticStats } from "../constants/staticData";


export const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const TOKEN_KEY = "suv_admin_token";

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export function formatError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Local in-memory state for fallback preview mode
let localProjects = [...STATIC_PROJECTS];
let localGallery = [...STATIC_GALLERY];

const getLocalStats = () => {
  const ongoing = localProjects.filter((p) => p.status === "ongoing" && p.group === "current").length;
  const completed = localProjects.filter((p) => p.status === "completed" && p.group === "current").length;
  const legacy = localProjects.filter((p) => p.group === "legacy").length;
  const total = localProjects.length;
  return { ongoing, completed, legacy, total };
};

// Public
export const fetchProjects = (params = {}) => 
  api.get("/projects", { params })
    .then((r) => r.data)
    .catch((err) => {
      console.warn("Backend API not reachable. Using static projects fallback.", err);
      let list = localProjects;
      if (params.status) list = list.filter((p) => p.status === params.status);
      if (params.group) list = list.filter((p) => p.group === params.group);
      return list;
    });

export const fetchStats = () => 
  api.get("/projects/stats")
    .then((r) => r.data)
    .catch((err) => {
      console.warn("Backend API not reachable. Using static stats fallback.", err);
      return getLocalStats();
    });

// Auth
export const login = (username, password) =>
  api.post("/auth/login", { username, password })
    .then((r) => r.data)
    .catch((err) => {
      if (err.message && (err.message.includes("Network Error") || !err.response)) {
        console.warn("Backend API not reachable. Checking static login fallback.");
        if (username === "admin" && (password === "SUVadmin@2026" || password === "admin123")) {
          return { token: "static-fallback-token", username: "admin" };
        }
        throw { response: { data: { detail: "Invalid username or password (offline mode)" } } };
      }
      throw err;
    });

export const fetchMe = () => 
  api.get("/auth/me")
    .then((r) => r.data)
    .catch((err) => {
      if (getToken() === "static-fallback-token") {
        return { username: "admin" };
      }
      throw err;
    });

export const changePassword = (current_password, new_password) =>
  api.post("/auth/change-password", { current_password, new_password })
    .then((r) => r.data)
    .catch((err) => {
      if (getToken() === "static-fallback-token") {
        console.log("Offline mode: Simulated password change to:", new_password);
        return { message: "Password updated successfully (offline simulation)" };
      }
      throw err;
    });

// Admin CRUD
export const createProject = (data) => 
  api.post("/projects", data)
    .then((r) => r.data)
    .catch((err) => {
      if (getToken() === "static-fallback-token") {
        const newProj = {
          id: `local-created-${Date.now()}`,
          name: data.name,
          client: data.client || "",
          location: data.location || "",
          sector: data.sector || "General",
          scope: data.scope || "",
          status: data.status || "completed",
          group: data.group || "current",
          featured: data.featured || false,
          order: localProjects.length
        };
        localProjects.push(newProj);
        return newProj;
      }
      throw err;
    });

export const updateProject = (id, data) => 
  api.put(`/projects/${id}`, data)
    .then((r) => r.data)
    .catch((err) => {
      if (getToken() === "static-fallback-token") {
        const idx = localProjects.findIndex((p) => p.id === id);
        if (idx !== -1) {
          localProjects[idx] = { ...localProjects[idx], ...data };
          return localProjects[idx];
        }
        throw new Error("Project not found");
      }
      throw err;
    });

export const deleteProject = (id) => 
  api.delete(`/projects/${id}`)
    .then((r) => r.data)
    .catch((err) => {
      if (getToken() === "static-fallback-token") {
        localProjects = localProjects.filter((p) => p.id !== id);
        return { message: "Project deleted" };
      }
      throw err;
    });

// Gallery
export const galleryImageUrl = (id) => {
  if (typeof id === "string" && (id.startsWith("/assets/") || id.startsWith("blob:") || id.startsWith("data:image/"))) {
    return id;
  }
  return `${API}/gallery/${id}/image`;
};

export const fetchGallery = () => 
  api.get("/gallery")
    .then((r) => r.data)
    .catch((err) => {
      console.warn("Backend API not reachable. Using static gallery fallback.", err);
      return localGallery;
    });

export const uploadGalleryImage = (file, category, caption = "") => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("category", category);
  fd.append("caption", caption);
  return api.post("/gallery", fd, { headers: { "Content-Type": "multipart/form-data" } })
    .then((r) => r.data)
    .catch((err) => {
      if (getToken() === "static-fallback-token") {
        const objectUrl = URL.createObjectURL(file);
        const newImg = {
          id: objectUrl,
          category: category || "General",
          caption: caption || "",
          order: localGallery.length
        };
        localGallery.push(newImg);
        return newImg;
      }
      throw err;
    });
};

export const updateGalleryImage = (id, data) => 
  api.put(`/gallery/${id}`, data)
    .then((r) => r.data)
    .catch((err) => {
      if (getToken() === "static-fallback-token") {
        const idx = localGallery.findIndex((img) => img.id === id);
        if (idx !== -1) {
          localGallery[idx] = { ...localGallery[idx], ...data };
          return localGallery[idx];
        }
        throw new Error("Image not found");
      }
      throw err;
    });

export const deleteGalleryImage = (id) => 
  api.delete(`/gallery/${id}`)
    .then((r) => r.data)
    .catch((err) => {
      if (getToken() === "static-fallback-token") {
        localGallery = localGallery.filter((img) => img.id !== id);
        return { message: "Image deleted" };
      }
      throw err;
    });
